/**
 * Datas de NEGÓCIO em ISO (YYYY-MM-DD), sempre no fuso LOCAL.
 *
 * `new Date('2026-07-20')` parseia como UTC e, em BRT, volta um dia. O mesmo
 * bug que date-text.tsx documenta na exibição aparece aqui na ida: se `hoje()`
 * errar por um dia, a divisa realizado/projetado do dashboard sai deslocada e o
 * gráfico mente. Por isso montamos e desmontamos a string por PARTES, nunca
 * pelo parser de ISO.
 */

const iso = (d: Date): string =>
  `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`

/** Hoje no fuso local, como YYYY-MM-DD. */
export function hojeIso(): string {
  return iso(new Date())
}

/**
 * Soma (ou subtrai, com `dias` negativo) dias a uma data ISO.
 *
 * O construtor Date(ano, mês, dia) normaliza sozinho: dia 32 vira o 1º do mês
 * seguinte, dia 0 vira o último do anterior. Vira e mexe de mês/ano sai de graça.
 */
export function somarDias(base: string, dias: number): string {
  const [a, m, d] = base.split('-').map(Number)
  return iso(new Date(a ?? 1970, (m ?? 1) - 1, (d ?? 1) + dias))
}

/**
 * Dias inteiros de `de` até `ate` (negativo se `ate` for anterior).
 *
 * Conta por `Date.UTC` das PARTES, nunca pelo parser de ISO: em UTC não há DST,
 * então a diferença é sempre um múltiplo exato de 86.400.000 ms e o arredondar
 * só absorve ruído de ponto flutuante — nunca uma hora de horário de verão que
 * faria a contagem errar por um dia.
 */
export function diffDias(de: string, ate: string): number {
  const [ay, am, ad] = de.split('-').map(Number)
  const [by, bm, bd] = ate.split('-').map(Number)
  const um = 86_400_000
  return Math.round(
    (Date.UTC(by ?? 1970, (bm ?? 1) - 1, bd ?? 1) - Date.UTC(ay ?? 1970, (am ?? 1) - 1, ad ?? 1)) /
      um,
  )
}

/**
 * Nº de meses-calendário TOCADOS por [de, ate], inclusivo nas pontas.
 *
 * Espelha exatamente o `_month_count` do backend (report_service): é a mesma
 * conta que decide o teto de 120 meses do relatório mensal. Precisa bater para o
 * front prever, sem ida à rede, se a janela cabe.
 */
export function mesesNaJanela(de: string, ate: string): number {
  const [ay, am] = de.split('-').map(Number)
  const [by, bm] = ate.split('-').map(Number)
  return ((by ?? 0) - (ay ?? 0)) * 12 + ((bm ?? 0) - (am ?? 0)) + 1
}

/**
 * Último dia do mês que está `offsetMeses` à frente do mês de `base`.
 *
 * `new Date(ano, mês+1, 0)` = dia 0 do mês seguinte = último dia do mês alvo; a
 * normalização do construtor resolve o transbordo de mês/ano sozinha.
 */
export function fimDoMesComOffset(base: string, offsetMeses: number): string {
  const [a, m] = base.split('-').map(Number)
  return iso(new Date(a ?? 1970, (m ?? 1) - 1 + offsetMeses + 1, 0))
}

/**
 * Primeiro dia do mês que está `offsetMeses` à frente do mês de `base`. Simétrico
 * de `fimDoMesComOffset` — juntos formam a janela [from, to] de UM mês-calendário
 * (a navegação ‹ mês › do extrato da conta).
 */
export function inicioDoMesComOffset(base: string, offsetMeses: number): string {
  const [a, m] = base.split('-').map(Number)
  return iso(new Date(a ?? 1970, (m ?? 1) - 1 + offsetMeses, 1))
}
